import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Heart, ArrowLeft, Loader2, Mail, CheckCircle2 } from "lucide-react";
import { apiClient } from "../lib/apiClient";


export default function ForgotPasswordPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await apiClient.post("authForgotPassword", {
        email,
        origin_url: window.location.origin,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-light noise-bg flex flex-col">
      <header className="p-4">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-stone-dark/70 hover:text-sage transition-colors"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to login</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-sage flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
            </Link>
            <h1 className="font-serif text-2xl font-semibold text-stone-dark mt-4">
              Reset your password
            </h1>
            <p className="text-stone-dark/60 mt-2">
              We'll send you a link to reset your password
            </p>
          </div>

          <Card className="border-stone shadow-lg" data-testid="forgot-password-card">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="font-serif text-xl text-center">Forgot Password</CardTitle>
              <CardDescription className="text-center">
                Enter the email address associated with your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              {success ? (
                <div className="text-center py-4" data-testid="forgot-password-success">
                  <div className="w-16 h-16 rounded-full bg-sage/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-sage" />
                  </div>
                  <h3 className="font-serif text-lg font-semibold text-stone-dark mb-2">Check your email</h3>
                  <p className="text-stone-dark/60 text-sm mb-6">
                    If an account exists for <strong>{email}</strong>, we've sent a password reset link. It may take a minute to arrive.
                  </p>
                  <div className="space-y-3">
                    <Button
                      variant="outline"
                      className="w-full rounded-full"
                      onClick={() => { setSuccess(false); setEmail(""); }}
                      data-testid="try-another-email"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Try a different email
                    </Button>
                    <Link to="/login">
                      <Button className="w-full bg-sage hover:bg-sage-dark text-white rounded-full mt-2" data-testid="back-to-login-btn">
                        Back to Sign In
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm" data-testid="forgot-password-error">
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">Email address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(""); }}
                      required
                      className="bg-stone-light border-stone focus:border-sage focus:ring-sage/20"
                      data-testid="forgot-password-email"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-sage hover:bg-sage-dark text-white rounded-full py-5 mt-6 font-medium"
                    disabled={loading}
                    data-testid="forgot-password-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send Reset Link"
                    )}
                  </Button>
                </form>
              )}

              <div className="mt-6 text-center">
                <p className="text-stone-dark/60 text-sm">
                  Remember your password?{" "}
                  <Link to="/login" className="text-sage hover:underline font-medium" data-testid="login-link">
                    Sign in
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-sage/5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
      </div>
    </div>
  );
}
